import React from 'react';
import { PlatformFormatCombo } from './platformDefinitions';

interface SafeZoneOverlayProps {
  combo: PlatformFormatCombo;
  imgRef: React.RefObject<HTMLImageElement>;
}

const SafeZoneOverlay: React.FC<SafeZoneOverlayProps> = ({ combo, imgRef }) => {
  if (!imgRef.current) return null;

  const { offsetWidth: boxW, offsetHeight: boxH, naturalWidth: nw, naturalHeight: nh } = imgRef.current;
  if (!boxW || !boxH || !nw || !nh) return null;

  // Compute the actual painted size (object-contain: scale to fit within box)
  const scale = Math.min(boxW / nw, boxH / nh);
  const w = Math.round(nw * scale);
  const h = Math.round(nh * scale);

  const { top, left, right, bottom } = combo.margins;

  const band: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    pointerEvents: 'none',
  };

  // Mirror the image's own positioning so the overlay sits exactly on top of it.
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: w,
        height: h,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {/* Top band — full width */}
      <div style={{ ...band, top: 0, left: 0, right: 0, height: `${top}%` }} />
      {/* Bottom band — full width */}
      <div style={{ ...band, bottom: 0, left: 0, right: 0, height: `${bottom}%` }} />
      {/* Left band — between top and bottom */}
      <div style={{ ...band, top: `${top}%`, left: 0, width: `${left}%`, height: `${100 - top - bottom}%` }} />
      {/* Right band — between top and bottom */}
      <div style={{ ...band, top: `${top}%`, right: 0, width: `${right}%`, height: `${100 - top - bottom}%` }} />
      {/* Label centered in the top band */}
      <div
        style={{
          position: 'absolute',
          top: `${top / 2}%`,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 11,
        }}
        className="bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded-full backdrop-blur-sm whitespace-nowrap"
      >
        {combo.platformLabel} · {combo.label} · {combo.aspectRatio}
      </div>
    </div>
  );
};

export default SafeZoneOverlay;
